import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import { datasetsApi } from '../lib/api';

export const NAME_MAX = 200; // backend: name must be 1-200 characters

/**
 * Inline editor for a dataset name. Enter / the check saves via PATCH /api/datasets/{id},
 * Escape / the cross cancels. onSaved(newName) runs after a successful save.
 */
export default function RenameField({ dataset, onSaved, onCancel, className = '' }) {
  const [value, setValue] = useState(dataset.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    const name = value.trim();
    if (!name) { setError('Name cannot be empty'); return; }
    if (name.length > NAME_MAX) { setError(`Name must be at most ${NAME_MAX} characters`); return; }
    if (name === dataset.name) { onCancel(); return; }
    setSaving(true);
    setError(null);
    try {
      await datasetsApi.update(dataset.id, { name });
      onSaved(name);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : e.message || 'Could not rename the dataset');
      setSaving(false);
    }
  };

  return (
    <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="text"
          value={value}
          maxLength={NAME_MAX}
          disabled={saving}
          aria-label="Dataset name"
          data-testid="rename-input"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
          className={`px-2 py-1 border border-border rounded-md bg-background min-w-0 flex-1 ${className}`}
        />
        <button type="button" onClick={save} disabled={saving} aria-label="Save name" data-testid="rename-save"
          className="p-1 rounded hover:bg-muted text-green-600"><Check className="w-4 h-4" /></button>
        <button type="button" onClick={onCancel} disabled={saving} aria-label="Cancel rename" data-testid="rename-cancel"
          className="p-1 rounded hover:bg-muted text-muted-foreground"><X className="w-4 h-4" /></button>
      </div>
      {error && <p className="text-xs text-red-600 mt-1" role="alert" data-testid="rename-error">{error}</p>}
    </div>
  );
}
